/**
 * Quiz Dialog Component
 * Manages a dialog-based quiz interface with multiple steps and result display
 * @extends HTMLElement
 */
class QuizDialog extends HTMLElement {
  constructor() {
    super();
    /** @type {string[]} */
    this.selectedOptions = [];
    /** @type {Object[]} */
    this.selectedOptionTitles = [];
    /** @type {HTMLDialogElement|null} */
    this.dialog = null;
  }

  /**
   * Set up event listeners and initialize dialog state
   */
  connectedCallback() {
    this.trigger = this.getAttribute('data-trigger');
    this.showInModal = this.getAttribute('data-modal') === 'true';
    this.dialog = this.querySelector('dialog');

    if (window.location.hash.includes(this.trigger) && this.showInModal) {
      this.showQuizDialog();
    }

    window.addEventListener('hashchange', this._onHashChange);

    this.querySelectorAll('[data-close]').forEach((button) =>
      button.addEventListener('click', this.closeQuizDialog)
    );

    this.addEventListener('quiz:option-selected', this.handleOptionSelected);
  }

  /**
   * Clean up event listeners and state when element is removed from DOM
   */
  disconnectedCallback() {
    window.removeEventListener('hashchange', this._onHashChange);

    this.querySelectorAll('[data-close]').forEach((button) =>
      button.removeEventListener('click', this.closeQuizDialog)
    );

    this.removeEventListener('quiz:option-selected', this.handleOptionSelected);
  }

  _onHashChange = () => {
    if (window.location.hash.includes(this.trigger)) this.showQuizDialog();
  };

  /**
   * @param {CustomEvent} event - The custom event
   */
  handleOptionSelected = (event) => {
    this.selectedOptions.push(event?.detail?.option ?? '');
    const title = event?.detail?.title ?? '';
    this.selectedOptionTitles.push(title);
  };

  /**
   * Display the quiz dialog and reset state
   */
  showQuizDialog = () => {
    this.selectedOptions = [];
    this.selectedOptionTitles = [];

    if (this.dialog) {
      this.dialog.showModal();
    }

    this.hideAllSteps();

    const firstStep = this.querySelector('[data-step="1"]');
    if (firstStep) {
      firstStep.removeAttribute('aria-hidden');
      firstStep.removeAttribute('inert');
      firstStep.setAttribute('tabindex', '-1');
      firstStep.focus();
    }

    this.hideResults();
    this.hideNoResult();
  };

  /**
   * Close the quiz dialog and clean up URL
   */
  closeQuizDialog = () => {
    try {
      if (this.dialog && this.dialog.open) {
        this.dialog.close();
        const payload = { dialogId: this.trigger };
        publish('dialog:close', payload);

        // Remove hash from URL if it matches the trigger
        if (window.location.hash.includes(this.trigger)) {
          history.pushState('', document.title, window.location.pathname + window.location.search);
        }
      }
    } catch (error) {
      console.error('Error closing dialog:', error);
    }
  };

  hideResults = () => {
    this.querySelectorAll('[data-quiz-result]')?.forEach((result) => {
      result.setAttribute('aria-hidden', 'true');
      result.setAttribute('inert', '');
    });
  };

  hideNoResult = () => {
    const noResultEl = this.querySelector('[data-no-result]');
    noResultEl?.setAttribute('aria-hidden', 'true');
    noResultEl?.setAttribute('inert', '');
  };

  showNoResult = () => {
    const noResultEl = this.querySelector('[data-no-result]');
    noResultEl?.removeAttribute('aria-hidden');
    noResultEl?.removeAttribute('inert');
  };

  hideAllSteps = () => {
    this.querySelectorAll('[data-step]')?.forEach((step) => {
      if (step.contains(document.activeElement) && document.activeElement !== document.body) {
        document.activeElement.blur();
      }
      step.setAttribute('aria-hidden', 'true');
      step.setAttribute('inert', '');
    });
  };

  /**
   * Show only the first step if rendered as a section (not modal)
   */
  showFirstStepIfSection() {
    this.hideAllSteps();
    const firstStep = this.querySelector('[data-step="1"]');
    if (firstStep) {
      firstStep.removeAttribute('aria-hidden');
      firstStep.removeAttribute('inert');
      firstStep.setAttribute('tabindex', '-1');
    }
    this.hideResults();
    this.hideNoResult();
  }

  /**
   * Get quiz results data from JSON script tag
   * @returns {Array<Object>}
   */
  getQuizResultsData() {
    const script = document.getElementById('quiz-result-data');
    if (!script) return [];
    try {
      let json = script.textContent.trim();
      if (!json.startsWith('[')) json = `[${json}]`;
      return JSON.parse(json);
    } catch (e) {
      console.error('Quiz: Failed to parse quiz results JSON during getQuizResultsData', e); // Keep one error for critical failure
      return [];
    }
  }

  /**
   * Render a quiz result using the template and data
   * @param {Object} resultData
   * @param {Array<string>} optionTitles
   * @returns {HTMLElement|null}
   */
  renderResult(resultData, optionTitles) {
    const template = document.getElementById('quiz-dynamic-result-template');
    if (!template) return null;

    const clone = template.content.cloneNode(true);
    const titleEl = clone.querySelector('[data-quiz-result-title]');
    if (titleEl && resultData.title) titleEl.textContent = resultData.title;

    const descEl = clone.querySelector('[data-quiz-result-description]');
    if (descEl && resultData.description) descEl.innerHTML = resultData.description;

    const imgEl = clone.querySelector('[data-quiz-result-image]');
    if (imgEl && resultData.image) {
      // Only update src if a specific image is provided
      imgEl.src = resultData.image;
    } // Otherwise, the template's fallback src will be used

    clone.querySelectorAll('[data-question-index]').forEach((cell) => {
      const idx = parseInt(cell.dataset.questionIndex, 10);
      if (!isNaN(idx) && optionTitles[idx]) {
        cell.textContent = optionTitles[idx];
      }
    });

    clone.querySelectorAll('[data-close]').forEach((button) => {
      button.addEventListener('click', this.closeQuizDialog);
    });

    const resultRoot = clone.querySelector('[data-quiz-result]');
    if (resultRoot) {
      resultRoot.setAttribute('aria-hidden', 'false');
      resultRoot.removeAttribute('inert');
      resultRoot.setAttribute('tabindex', '0');
      resultRoot.classList.add('quiz-dynamic-result-rendered');
    }
    return clone;
  }

  showResults = () => {
    if (!this.quizResultsData) {
      this.quizResultsData = this.getQuizResultsData();
    }

    const selectedOptionsSet = new Set(
      this.selectedOptions.map((slug) => slug.toLowerCase().trim())
    );

    let matchingResult = null;
    if (this.quizResultsData && this.quizResultsData.length > 0) {
      for (const result of this.quizResultsData) {
        const resultOptionsSet = new Set(
          (result.selected_options || []).map((opt) => opt.slug.toLowerCase().trim())
        );
        if (
          selectedOptionsSet.size === resultOptionsSet.size &&
          [...selectedOptionsSet].every((option) => resultOptionsSet.has(option))
        ) {
          matchingResult = result;
          break;
        }
      }
    }

    this.hideAllSteps();

    const oldResult = this.querySelector('.quiz-dynamic-result-rendered');
    if (oldResult) {
      oldResult.remove();
    }

    if (matchingResult) {
      const rendered = this.renderResult(matchingResult, this.selectedOptionTitles);
      if (rendered) {
        const container = this.querySelector('dialog,div'); // Main container of quiz (dialog or div)
        if (container) {
          container.appendChild(rendered);
          const focusEl = container.querySelector('.quiz-dynamic-result-rendered');
          if (focusEl) focusEl.focus();
        }
      }
    } else {
      this.showNoResult();
      const noResult = this.querySelector('[data-no-result]');
      if (noResult) {
        noResult.setAttribute('tabindex', '0');
        noResult.focus();
      }
    }
  };
}

/**
 * Quiz Options Component
 * Handles option selection and step navigation
 * @extends HTMLElement
 */
class QuizOptions extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick = (event) => {
    const option = event.target.closest('[data-quiz-option]');
    if (option) this.handleOptionClick(option);
  };

  handleOptionClick = (option) => {
    const { quizOption, quizOptionTitle: attributeTitle } = option.dataset;
    const optionTitle = attributeTitle ?? quizOption.replace(/-/g, ' ');

    this.dispatchEvent(
      new CustomEvent('quiz:option-selected', {
        bubbles: true,
        detail: {
          option: quizOption,
          title: optionTitle,
        },
      })
    );

    const currentStep = option.closest('[data-step]');
    const quizDialog = this.closest('quiz-dialog');

    if (
      currentStep &&
      currentStep.contains(document.activeElement) &&
      document.activeElement !== document.body
    ) {
      document.activeElement.blur();
    }

    currentStep?.setAttribute('aria-hidden', 'true');
    currentStep?.setAttribute('inert', '');

    const nextStepNumber = Number(currentStep?.getAttribute('data-step')) + 1;
    const nextStep = quizDialog?.querySelector(`[data-step="${nextStepNumber}"]`);

    if (nextStep) {
      nextStep.removeAttribute('aria-hidden');
      nextStep.removeAttribute('inert');
      nextStep.setAttribute('tabindex', '-1');
      nextStep.focus();
    } else {
      quizDialog?.showResults();
    }
  };
}

/**
 * Quiz Restart Component
 * Handles restarting the quiz
 * @extends HTMLElement
 */
class QuizRestart extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick = () => {
    this.closest('quiz-dialog')?.showQuizDialog();

    // Hide intro if it's visible
    this.closest('quiz-dialog')
      ?.querySelector('[data-quiz-intro]')
      ?.setAttribute('aria-hidden', 'true');
  };
}

/**
 * Quiz Back Component
 * Handles navigating back to the previous step
 * @extends HTMLElement
 */
class QuizBack extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick = () => {
    this.handleBackClick();
  };

  handleBackClick = () => {
    const currentStep = this.closest('[data-step]');
    const quizDialog = this.closest('quiz-dialog');

    if (!currentStep || !quizDialog) return;

    quizDialog.selectedOptions.pop();
    quizDialog.selectedOptionTitles.pop();

    if (currentStep.contains(document.activeElement) && document.activeElement !== document.body) {
      document.activeElement.blur();
    }

    currentStep?.setAttribute('aria-hidden', 'true');
    currentStep?.setAttribute('inert', '');

    const previousStepNumber = Number(currentStep.getAttribute('data-step')) - 1;
    const previousStep = quizDialog?.querySelector(`[data-step="${previousStepNumber}"]`);

    if (previousStep) {
      previousStep.removeAttribute('aria-hidden');
      previousStep.removeAttribute('inert');
      previousStep.setAttribute('tabindex', '-1');
      previousStep.focus();
    }
  };
}

customElements.define('quiz-dialog', QuizDialog);
customElements.define('quiz-options', QuizOptions);
customElements.define('quiz-restart', QuizRestart);
customElements.define('quiz-back', QuizBack);
